
import { Camera, Edit, MapPin, Link2, Users, Instagram, Twitter, Facebook, Globe, Youtube } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { EditProfile } from "@/components/EditProfile";
import { formatSocialMediaUrl } from "@/components/webnovel/sidebar/utils";

type SocialMediaPlatform = "instagram" | "twitter" | "facebook" | "youtube" | "website";

interface SocialMediaLink {
  platform: SocialMediaPlatform;
  url: string;
}

interface ProfileHeaderMobileProps {
  profile: {
    id: string;
    username?: string;
    avatar_url?: string;
    banner_url?: string;
    bio?: string;
    website?: string;
    location?: string;
    social_links?: SocialMediaLink[];
  };
  currentUser: string | null;
  isFollowing: boolean;
  followersCount: number;
  followingCount: number;
  onProfileUpdate: () => void;
  onFollowToggle: () => void;
  onLogout: () => void;
  handleBannerFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleAvatarFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  bannerFileRef: React.RefObject<HTMLInputElement>;
  avatarFileRef: React.RefObject<HTMLInputElement>;
  isOwnProfile: boolean;
}

export function ProfileHeaderMobile({
  profile,
  currentUser,
  isFollowing,
  followersCount,
  followingCount,
  onProfileUpdate,
  onFollowToggle,
  onLogout,
  handleBannerFileChange,
  handleAvatarFileChange,
  bannerFileRef,
  avatarFileRef,
  isOwnProfile
}: ProfileHeaderMobileProps) {
  const getSocialIcon = (platform: SocialMediaPlatform) => {
    switch (platform) {
      case "instagram": return <Instagram className="h-3 w-3" />;
      case "twitter": return <Twitter className="h-3 w-3" />;
      case "facebook": return <Facebook className="h-3 w-3" />;
      case "youtube": return <Youtube className="h-3 w-3" />;
      case "website": return <Globe className="h-3 w-3" />;
    }
  };

  return (
    <div className="pt-16 pb-4 flex flex-col items-center">
      <div className="w-full relative">
        <div 
          className="w-full h-40 bg-gradient-to-r from-primary/20 to-secondary/20"
          style={profile.banner_url ? {
            backgroundImage: `url(${profile.banner_url})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          } : {}}
        >
          {currentUser === profile.id && (
            <label 
              htmlFor="banner-upload" 
              className="absolute bottom-2 right-2 p-2 bg-background/70 rounded-full cursor-pointer hover:bg-background transition-colors"
            >
              <Camera className="h-4 w-4 text-foreground" />
              <input 
                id="banner-upload" 
                type="file" 
                accept="image/*" 
                className="hidden" 
                ref={bannerFileRef}
                onChange={handleBannerFileChange}
              />
            </label>
          )}
        </div>
        
        <div className="px-4 -mt-16 flex flex-col items-center">
          <div className="relative inline-block">
            <Avatar className="h-28 w-28 border-4 border-background shadow-lg">
              <AvatarImage src={profile.avatar_url} />
              <AvatarFallback className="text-2xl">
                {profile.username?.charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            
            {currentUser === profile.id && (
              <label 
                htmlFor="avatar-upload" 
                className="absolute bottom-1 right-1 p-2 bg-background/80 rounded-full cursor-pointer hover:bg-background transition-colors shadow"
              >
                <Edit className="h-4 w-4 text-foreground" />
                <input 
                  id="avatar-upload" 
                  type="file" 
                  accept="image/*" 
                  className="hidden"
                  ref={avatarFileRef}
                  onChange={handleAvatarFileChange}
                />
              </label>
            )}
          </div>
          
          <div className="mt-4 flex flex-col items-center w-full space-y-3">
            <div className="flex flex-col items-center space-y-1">
              <h1 className="text-2xl font-bold text-foreground">{profile.username}</h1>
              {profile.location && (
                <div className="flex items-center gap-1 text-sm text-foreground/60">
                  <MapPin className="h-4 w-4" />
                  <span>{profile.location}</span>
                </div>
              )}
            </div>

            <div className="flex items-center gap-6">
              <div className="flex flex-col items-center">
                <span className="font-bold">{followersCount}</span>
                <span className="text-sm text-foreground/60">Seguidores</span>
              </div>
              <div className="flex flex-col items-center">
                <span className="font-bold">{followingCount}</span>
                <span className="text-sm text-foreground/60">Seguindo</span>
              </div>
            </div>

            {currentUser && (
              <div className="w-full flex justify-center gap-2">
                {currentUser === profile.id ? (
                  <div className="flex gap-2">
                    <EditProfile
                      isOpen={false}
                      onOpenChange={() => {}}
                      onProfileUpdate={onProfileUpdate}
                    />
                    <Button
                      variant="outline"
                      onClick={onLogout}
                    >
                      Sair
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant={isFollowing ? "outline" : "default"}
                    className="w-full max-w-xs"
                    onClick={onFollowToggle}
                  >
                    {isFollowing ? "Deixar de Seguir" : "Seguir"}
                  </Button>
                )}
              </div>
            )}

            {profile.website && (
              <a 
                href={profile.website.startsWith('http') ? profile.website : `https://${profile.website}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-xs hover:text-primary transition-colors mt-1"
              >
                <Link2 className="h-3 w-3" />
                <span>{formatSocialMediaUrl(profile.website)}</span>
              </a>
            )}
            
            {profile.social_links && profile.social_links.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2 justify-center">
                {profile.social_links.map((link, index) => (
                  <a
                    key={index}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 px-2 py-1 bg-muted rounded-full text-xs hover:bg-muted/80 transition-colors"
                  >
                    {getSocialIcon(link.platform)}
                    <span>{formatSocialMediaUrl(link.url)}</span>
                  </a>
                ))}
              </div>
            )}
            
            {profile.bio && (
              <div className="mt-3 text-sm text-foreground/80 text-center max-w-md">
                {profile.bio}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
